import type React from 'react'

/**
 * iOS sends no `dblclick` for touch once page zoom is off (`touch-action` on <html>), so
 * every double-click action also needs a touch double-tap: a second touch `pointerup` on
 * the same target within 350ms. Android does send `dblclick` after a double-tap, so
 * `onDoubleClick` skips the one that trails a double-tap already handled.
 */
let lastTap: { key: unknown; time: number } | null = null
let lastDoubleTap = -Infinity

/** `key` names the target when re-renders may swap the element (grid cells); defaults to the element. */
export function isDoubleTap(e: React.PointerEvent, key: unknown = e.currentTarget): boolean {
  if (e.pointerType !== 'touch') return false
  const now = e.timeStamp
  if (lastTap && lastTap.key === key && now - lastTap.time < 350) {
    lastTap = null
    lastDoubleTap = now
    return true
  }
  lastTap = { key, time: now }
  return false
}

/** True for the `dblclick` Android sends right after a double-tap `isDoubleTap` already handled. */
export function followsDoubleTap(e: React.UIEvent): boolean {
  return e.timeStamp - lastDoubleTap < 600
}

/**
 * `onDoubleClick` plus the matching touch `onPointerUp`, for an element with no
 * `onPointerUp` of its own. Spread it in place of `onDoubleClick`.
 */
export function doubleTapProps<E extends Element>(
  handler: ((e: React.MouseEvent<E>) => void) | undefined,
): { onDoubleClick?: (e: React.MouseEvent<E>) => void; onPointerUp?: (e: React.PointerEvent<E>) => void } {
  if (!handler) return {}
  return {
    onDoubleClick: (e) => { if (!followsDoubleTap(e)) handler(e) },
    onPointerUp: (e) => { if (isDoubleTap(e)) handler(e) },
  }
}
